import { ApolloProvider } from '@apollo/client/react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { initializeAuth } from '@choco-admin/auth';
import { initializeApolloClient } from '@choco-admin/graphql-client';
import {
  getCurrentEnvironmentConfig,
  maybeAddGooglePlacesScriptTag,
} from '@choco-admin/utils';
import { HeroUIProvider } from '@heroui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import App from './App';
import { GOOGLE_PLACES_API_KEY } from './config';
import { ThemeProvider } from './contexts/ThemeContext';
import { createLaunchDarklyProvider, IdentifyPortalUser } from './featureFlags';
import { SomethingWentWrongPage } from './pages/SomethingWentWrongPage';
import { setupUrlWatcher } from './utils/urlDebugger';
import './styles/index.css';

// Initialize authentication configuration with dynamic environment
const initializeAuthWithEnvironment = () => {
  try {
    const envConfig = getCurrentEnvironmentConfig();

    // Build redirect URLs - these MUST match what's configured in Cognito App Client
    const redirectSignIn = `${window.location.origin}/`;
    const redirectSignOut = `${window.location.origin}/`;

    initializeAuth({
      region: envConfig.region,
      userPoolId: envConfig.userPoolId,
      userPoolWebClientId: envConfig.userPoolWebClientId,
      identityPoolId: envConfig.identityPoolId,
      // OAuth configuration for Google login
      oauth: {
        domain: envConfig.oauthDomain,
        scope: ['openid', 'email', 'aws.cognito.signin.user.admin'],
        redirectSignIn,
        redirectSignOut,
        responseType: 'code',
      },
    });

    // Store environment config globally for debugging
    // eslint-disable-next-line no-underscore-dangle
    (window as any).__CHOCO_ENV_CONFIG__ = envConfig;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to initialize auth:', error);

    // In development, we might want to continue without auth for testing
    if (process.env.NODE_ENV !== 'development') {
      throw error;
    }
  }
};

// Initialize authentication
initializeAuthWithEnvironment();

// A deploy replaces the hashed chunk files an already-open tab still points at,
// so a dynamic import can 404 mid-session and the interaction behind it does
// nothing at all. Vite raises `vite:preloadError` for exactly that. Only the
// environment context and the environment switcher import lazily today, but
// route-level splitting will widen this the moment it lands.
window.addEventListener('vite:preloadError', () => {
  // The other way this fires is a dead connection, where reloading swaps the
  // whole app for the browser's error page. The offline banner is already
  // saying what to do, so leave the user on the page they have.
  if (!navigator.onLine) return;

  // Not "the portal has been updated": a preload can also fail on a flaky
  // connection, so asserting a deploy would be a guess about the cause.
  const message =
    'Part of the portal could not be loaded. Reload to try again?';

  // eslint-disable-next-line no-alert -- runs outside the React tree, so there is no mounted component to route this through
  if (window.confirm(message)) {
    window.location.reload();
  }
});

// Setup URL debugging to capture OAuth redirects
if (import.meta.env.DEV) {
  setupUrlWatcher();

  // Make URL debugger available globally for manual debugging
  (window as any).debugUrl = () => {
    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
    const { captureUrlParams } = require('./utils/urlDebugger');
    return captureUrlParams();
  };
}

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');

const root = createRoot(container);
const queryClient = new QueryClient();

// Initialize Apollo Client and render app
async function renderApp() {
  const envConfig = getCurrentEnvironmentConfig();

  // Create auth token getter function
  const getAuthToken = async (): Promise<string | undefined> => {
    try {
      const authSession = await fetchAuthSession();
      return authSession.tokens?.idToken?.toString();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to get auth token:', error);
      return undefined;
    }
  };

  const apolloClient = await initializeApolloClient(envConfig, getAuthToken);

  if (!apolloClient) {
    throw new Error('Failed to initialize Apollo Client');
  }

  // Started here rather than inside the tree because the flags have to be
  // resolved before the first render: a control that flashes editable and then
  // locks is worse than one that is locked from the start. The environment is
  // already known at this point, and switching it reloads the page, so one start
  // per load is all this needs.
  const LaunchDarklyProvider = await createLaunchDarklyProvider(envConfig);

  const app = (
    <>
      <IdentifyPortalUser />
      <App />
    </>
  );

  root.render(
    <React.StrictMode>
      <ThemeProvider defaultTheme="dark">
        <HeroUIProvider>
          <QueryClientProvider client={queryClient}>
            <ApolloProvider client={apolloClient}>
              <BrowserRouter>
                <NuqsAdapter>
                  {LaunchDarklyProvider ? (
                    <LaunchDarklyProvider>{app}</LaunchDarklyProvider>
                  ) : (
                    app
                  )}
                </NuqsAdapter>
              </BrowserRouter>
            </ApolloProvider>
          </QueryClientProvider>
        </HeroUIProvider>
      </ThemeProvider>
    </React.StrictMode>
  );
}

// Initialize Google Places script - using same pattern as choco-admin-buyers
maybeAddGooglePlacesScriptTag({ apiKey: GOOGLE_PLACES_API_KEY });

// Start the app
renderApp().catch((error) => {
  // eslint-disable-next-line no-console
  console.error('Failed to initialize app:', error);

  // Render error fallback
  root.render(
    <React.StrictMode>
      <SomethingWentWrongPage />
    </React.StrictMode>
  );
});
